import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader, ArrowLeft, Shield, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { API_BASE } from '../../../config/api';

const ApprovedDeposits = () => {
  const { depositId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const userEmail = searchParams.get('user');
  const amount = searchParams.get('amount');
  const network = searchParams.get('network');
  const userName = searchParams.get('userName');
  const transactionId = searchParams.get('transactionId');
  
  const [isApproving, setIsApproving] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [error, setError] = useState(null);
  const [authStatus, setAuthStatus] = useState('checking'); // 'checking' | 'authorized' | 'unauthorized'

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token') || localStorage.getItem('eraX_auth_token');
      
      console.log('🔍 Checking admin auth...');
      console.log('Token exists:', !!token);
      
      if (!token) {
        console.log('❌ No token found, redirecting to login');
        // Save current URL for redirect after login
        const currentUrl = window.location.pathname + window.location.search;
        localStorage.setItem('adminRedirectUrl', currentUrl);
        console.log('💾 Saved redirect URL:', currentUrl);
        
        // Redirect to login
        navigate('/adminLogin');
        return;
      }

      try {
        const response = await axios.get(`${API_BASE}/api/identity/current-user`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        console.log('✅ Auth response:', response.data);

        if (response.data.success && response.data.user?.isAdmin) {
          console.log('✅ User is admin');
          setAuthStatus('authorized');
        } else {
          console.log('❌ User is not admin');
          setAuthStatus('unauthorized');
          setError("You do not have admin privileges.");
        }
      } catch (err) {
        console.error('❌ Auth check failed:', err);
        console.error('Error details:', err.response?.data);
        
        // Clear invalid token
        localStorage.removeItem('token');
        localStorage.removeItem('eraX_auth_token');
        
        // Save URL and redirect to login
        const currentUrl = window.location.pathname + window.location.search;
        localStorage.setItem('adminRedirectUrl', currentUrl);
        navigate('/adminLogin');
      }
    };

    checkAuth();
  }, [navigate]);

  const handleApprove = async () => {
    console.log('🚀 Starting approval process...');
    setIsApproving(true);
    setError(null);
    
    const token = localStorage.getItem('token') || localStorage.getItem('eraX_auth_token');

    try {
      console.log('📤 Sending approval request to backend...');
      console.log('Deposit ID:', depositId);
      console.log('API URL:', `${API_BASE}/api/deposit/confirm/${depositId}`);
      
      const response = await axios.post(
        `${API_BASE}/api/deposit/confirm/${depositId}`,
        { adminNotes: 'Approved via email link' },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('✅ Backend response:', response.data);

      if (response.data.success) {
        console.log('✅ Approval successful!');
        setIsApproved(true);
        localStorage.removeItem('pendingDepositId');
      } else {
        console.error('❌ Backend returned error:', response.data);
        setError(response.data.message || "Approval failed.");
        setIsApproving(false);
      }
    } catch (err) {
      console.error('❌ Approval API Error:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
        localStorage.removeItem('token');
        const currentUrl = window.location.pathname + window.location.search;
        localStorage.setItem('adminRedirectUrl', currentUrl);
        setTimeout(() => navigate('/adminLogin'), 2000);
      } else {
        setError(err.response?.data?.message || 'Failed to approve deposit. Check console for details.');
      }
      setIsApproving(false);
    }
  };

  // Loading state
  if (authStatus === 'checking') {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0f172a',
        color: '#fff'
      }}>
        <Loader size={40} className="spin" style={{ marginRight: '15px' }} />
        <h2>Verifying Admin Access...</h2>
      </div>
    );
  }

  // Unauthorized state
  if (authStatus === 'unauthorized') {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0f172a',
        color: '#fff'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '40px',
          background: '#1e293b',
          borderRadius: '12px'
        }}>
          <Shield size={48} color="#f3ba2f" style={{ marginBottom: '20px' }} />
          <h2>Access Denied</h2>
          <p style={{ color: '#94a3b8' }}>{error}</p>
          <button
            onClick={() => navigate('/adminLogin')}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              background: '#f3ba2f',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Login as Admin
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (isApproved) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0f172a',
        color: '#fff'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '40px',
          background: '#1e293b',
          borderRadius: '12px',
          border: '2px solid #10b981'
        }}>
          <CheckCircle size={64} color="#10b981" style={{ marginBottom: '20px' }} />
          <h2 style={{ color: '#10b981' }}>Deposit Approved Successfully!</h2>
          <p style={{ color: '#94a3b8' }}>Funds have been credited and investment created.</p>
          <button
            onClick={() => navigate('/admin/dashboard')}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Main approval UI
  return (
    <div style={{
      minHeight: '100vh',
      padding: '40px 20px',
      background: '#0f172a',
      color: '#fff',
      display: 'flex',
      justifyContent: 'center'
    }}>
      <div style={{ maxWidth: '600px', width: '100%' }}>
        <button
          onClick={() => navigate('/admin/dashboard')}
          style={{
            background: 'transparent',
            border: '1px solid #334155',
            color: '#94a3b8',
            padding: '8px 16px',
            borderRadius: '6px',
            cursor: 'pointer',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <ArrowLeft size={16} /> Back to Dashboard
        </button>

        <div style={{
          background: '#1e293b',
          borderRadius: '12px',
          padding: '30px',
          border: '1px solid #334155'
        }}>
          <h1 style={{ marginBottom: '20px', color: '#f3ba2f' }}>
            Confirm Deposit Approval
          </h1>
          
          <div style={{
            background: '#0f172a',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <p style={{ margin: '5px 0', color: '#94a3b8' }}>
              User: <strong style={{ color: '#fff' }}>{userName}</strong>
            </p>
            <p style={{ margin: '5px 0', color: '#94a3b8' }}>
              Email: <strong style={{ color: '#fff' }}>{userEmail}</strong>
            </p>
            <p style={{ margin: '5px 0', color: '#94a3b8' }}>
              Amount: <strong style={{ color: '#10b981', fontSize: '1.2em' }}>
                ${parseFloat(amount).toFixed(2)}
              </strong>
            </p>
            <p style={{ margin: '5px 0', color: '#94a3b8' }}>
              Network: <strong style={{ color: '#fff' }}>{network}</strong>
            </p>
            <p style={{ margin: '5px 0', color: '#94a3b8' }}>
              Deposit ID: <strong style={{ color: '#f3ba2f', fontFamily: 'monospace', fontSize: '0.9em' }}>
                {depositId}
              </strong>
            </p>
          </div>

          {error && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid #ef4444',
              color: '#ef4444',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <AlertCircle size={16} /> {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => navigate('/admin/dashboard')}
              disabled={isApproving}
              style={{
                padding: '12px 24px',
                background: '#334155',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleApprove}
              disabled={isApproving}
              style={{
                padding: '12px 24px',
                background: '#10b981',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {isApproving ? (
                <>
                  <Loader size={16} className="spin" /> Processing...
                </>
              ) : (
                <>
                  <CheckCircle size={16} /> Approve & Fund User
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovedDeposits;